import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Toast } from "primereact/toast";
import { axiosAuth } from "../../utils/axiosInstance";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import Cookies from "js-cookie";
import UserAccountLayout from "./userAccount/UserAccountLayout";
import "../../css/modal.css";

const countryOptions = [
  { value: "Country", label: "Country" },
  { value: "India", label: "India" },
  { value: "Bangladesh", label: "Bangladesh" },
  { value: "Nepal", label: "Nepal" },
  { value: "Pakistan", label: "Pakistan" },
];

const Userprofile = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      address: "",
      state: "",
      city: "",
      zipcode: "",
      country: { value: "Country", label: "Country" },
    },
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [isProfileSaved, setIsProfileSaved] = useState(
    localStorage.getItem("isProfileSaved") === "true",
  );
  const [isPhoneNumberSet, setIsPhoneNumberSet] = useState(
    localStorage.getItem("isPhoneNumberSet") === "true",
  );
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const BASE_IMAGE_URL = process.env.REACT_APP_IMG_URL;

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = Cookies.get("USER_TOKEN");
        if (!token) throw new Error("No user token found.");

        const response = await axiosAuth.get("get-user-details");
        if (response.status === 200 && response.data?.user) {
          const user = response.data.user;
          const phoneNumber = user.contact || user.phone || "";
          const hasPhoneNumber = !!phoneNumber;
          setIsPhoneNumberSet(hasPhoneNumber);
          localStorage.setItem("isPhoneNumberSet", hasPhoneNumber);

          reset({
            name: user.name || "",
            email: user.email || "",
            phoneNumber,
            address: user.address || "",
            state: user.state || "",
            city: user.city || "",
            zipcode: user.zipcode || "",
            country:
              countryOptions.find((option) => option.value === user.country) ||
              countryOptions[0],
          });

          if (user.image) {
            setProfileImage(
              user.image.startsWith("http")
                ? user.image
                : `${BASE_IMAGE_URL}${user.image}`,
            );
          } else {
            setProfileImage(null);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        showToast(
          "error",
          "Could not load profile",
          error.response?.data?.message || error.message,
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [reset, location, BASE_IMAGE_URL]);

  useEffect(() => {
    localStorage.setItem("isProfileSaved", isProfileSaved);
    localStorage.setItem("isPhoneNumberSet", isPhoneNumberSet);
  }, [isProfileSaved, isPhoneNumberSet]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast(
        "warn",
        "Invalid file",
        "Please upload an image file (JPG, PNG, SVG).",
      );
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast("warn", "File too large", "Image must be 5MB or smaller.");
      return;
    }
    setProfileImage(URL.createObjectURL(file));
    setProfileImageFile(file);
  };

  const handleReset = () => {
    const currentValues = getValues();
    reset({
      name: "",
      address: "",
      state: "",
      city: "",
      zipcode: "",
      country: { value: "Country", label: "Country" },
      email: "",
      phoneNumber: isPhoneNumberSet ? currentValues.phoneNumber : "",
    });
    setProfileImage(null);
    setProfileImageFile(null);
    const fileInput = document.getElementById("ua-profile-file");
    if (fileInput) fileInput.value = "";
  };

  const onSubmit = async (data) => {
    setSaving(true);
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("email", data.email);
    if (!isPhoneNumberSet) {
      formData.append("contact", data.phoneNumber);
      setIsPhoneNumberSet(!!data.phoneNumber);
      localStorage.setItem("isPhoneNumberSet", !!data.phoneNumber);
    }
    formData.append("address", data.address);
    formData.append("state", data.state);
    formData.append("city", data.city);
    formData.append("zipcode", data.zipcode);
    formData.append("country", data.country?.value || "");
    if (profileImageFile) formData.append("image", profileImageFile);

    try {
      const response = await axiosAuth.post("profileUpdate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        showToast(
          "success",
          "Profile updated",
          "Your profile has been saved successfully.",
        );
        setIsProfileSaved(true);
        const updatedResponse = await axiosAuth.get("get-user-details");
        if (updatedResponse.status === 200 && updatedResponse.data?.user) {
          const user = updatedResponse.data.user;
          const phoneNumber = user.contact || user.phone || "";
          setIsPhoneNumberSet(!!phoneNumber);
          localStorage.setItem("isPhoneNumberSet", !!phoneNumber);
          reset({
            name: user.name || "",
            email: user.email || "",
            phoneNumber,
            address: user.address || "",
            state: user.state || "",
            city: user.city || "",
            zipcode: user.zipcode || "",
            country:
              countryOptions.find((option) => option.value === user.country) ||
              countryOptions[0],
          });
          if (user.image) {
            setProfileImage(
              user.image.startsWith("http")
                ? user.image
                : `${BASE_IMAGE_URL}${user.image}`,
            );
          } else {
            setProfileImage(null);
          }
        }
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      showToast(
        "error",
        "Update failed",
        error.response?.data?.error || error.message,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = () => {
    setDeleteConfirmOpen(true);
  };
  const confirmDeleteAccount = async () => {
    setDeletingAccount(true);
    let shouldClose = true;

    try {
      const bookingsResponse = await axiosAuth.get("my-bookings");

      if (
        bookingsResponse.status === 200 &&
        Array.isArray(bookingsResponse.data)
      ) {
        const activeBookings = bookingsResponse.data.filter(
          (booking) =>
            booking.status === "pending" || booking.status === "confirmed",
        );

        if (activeBookings.length > 0) {
          shouldClose = false;

          showToast(
            "warn",
            "Active bookings",
            "Cancel your active bookings before deleting your account.",
          );

          return;
        }
      }

      // Get logged-in user id
      const userResponse = await axiosAuth.get("get-user-details");
      const userId = userResponse.data.user.user_id;
      // Delete account API
      const response = await axiosAuth.delete(`delete-user-account/${userId}`);

      if (response.status === 200) {
        showToast(
          "success",
          "Account deleted",
          "Your account has been removed successfully.",
        );

        Cookies.remove("USER_TOKEN");
        localStorage.removeItem("isProfileSaved");
        localStorage.removeItem("loginType");
        localStorage.removeItem("isPhoneNumberSet");

        navigate("/");
      }
    } catch (error) {
      console.error("Error deleting account:", error);

      showToast(
        "error",
        "Delete failed",
        error.response?.data?.error || error.message,
      );
    } finally {
      setDeletingAccount(false);

      if (shouldClose) {
        setDeleteConfirmOpen(false);
      }
    }
  };

  return (
    <>
      <Toast ref={toast} position="bottom-right" />
      {deleteConfirmOpen ? (
        <dialog
          className="ua-confirm-overlay"
          open
          aria-modal="true"
          aria-labelledby="delete-account-title"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === "Escape" && !deletingAccount) {
              setDeleteConfirmOpen(false);
            }
          }}
          onClick={(e) => {
            // Only close when clicking the backdrop, not the modal content.
            if (!deletingAccount && e.target === e.currentTarget) {
              setDeleteConfirmOpen(false);
            }
          }}
        >
          <div className="ua-confirm-modal">
            <h3 id="delete-account-title" className="ua-confirm-modal__title">
              Delete account
            </h3>
            <p className="ua-confirm-modal__message">
              Are you sure you want to delete your account? This action cannot
              be undone.
            </p>
            <div className="ua-confirm-modal__actions">
              <button
                type="button"
                className="ua-btn ua-btn--ghost"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deletingAccount}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ua-btn ua-btn--danger"
                onClick={confirmDeleteAccount}
                disabled={deletingAccount}
              >
                {deletingAccount ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </dialog>
      ) : null}
      <UserAccountLayout
        active="profile"
        title="Profile"
        subtitle="Manage your personal details and account settings."
      >
        <section className="ua-card">
          {loading ? (
            <div className="ua-loading">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
              Loading profile…
            </div>
          ) : (
            <form
              id="profile-form"
              className="ua-profile"
              onSubmit={handleSubmit(onSubmit)}
            >
              <label htmlFor="ua-profile-file" className="ua-avatar-upload">
                <span className="ua-avatar-upload__preview">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      onError={() => setProfileImage(null)}
                    />
                  ) : (
                    <i className="fa-solid fa-camera" aria-hidden="true" />
                  )}
                </span>
                <p className="ua-avatar-upload__hint">
                  <i className="fa-solid fa-pen" aria-hidden="true" /> Upload
                  photo
                </p>
                <input
                  type="file"
                  id="ua-profile-file"
                  accept="image/*"
                  onChange={handleFileChange}
                  hidden
                />
              </label>

              <div className="ua-form-grid">
                <div className="ua-field">
                  <label htmlFor="name">Full name</label>
                  <input
                    type="text"
                    id="name"
                    placeholder="Your name"
                    {...register("name", { required: "Name is required" })}
                  />
                  {errors.name ? (
                    <p className="ua-field__error">{errors.name.message}</p>
                  ) : null}
                </div>

                <div className="ua-field">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    placeholder="you@email.com"
                    {...register("email", { required: "Email is required" })}
                  />
                  {errors.email ? (
                    <p className="ua-field__error">{errors.email.message}</p>
                  ) : null}
                </div>

                <div className="ua-field">
                  <label htmlFor="phoneNumber">Phone</label>
                  <input
                    type="text"
                    id="phoneNumber"
                    placeholder="10-digit number"
                    maxLength={10}
                    disabled={isPhoneNumberSet}
                    {...register("phoneNumber", {
                      required: !isPhoneNumberSet
                        ? "Phone number is required"
                        : false,
                      pattern: !isPhoneNumberSet
                        ? {
                            value: /^[0-9]{10}$/,
                            message: "Phone must be exactly 10 digits",
                          }
                        : undefined,
                    })}
                  />
                  {errors.phoneNumber ? (
                    <p className="ua-field__error">
                      {errors.phoneNumber.message}
                    </p>
                  ) : null}
                </div>

                <div className="ua-form-section">
                  <h3>Address</h3>
                </div>

                <div className="ua-field ua-form-grid--full">
                  <label htmlFor="address">Street address</label>
                  <input
                    type="text"
                    id="address"
                    placeholder="House no., street, area"
                    {...register("address", {
                      required: "Address is required",
                    })}
                  />
                  {errors.address ? (
                    <p className="ua-field__error">{errors.address.message}</p>
                  ) : null}
                </div>

                <div className="ua-field">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    id="city"
                    placeholder="City"
                    {...register("city", { required: "City is required" })}
                  />
                  {errors.city ? (
                    <p className="ua-field__error">{errors.city.message}</p>
                  ) : null}
                </div>

                <div className="ua-field">
                  <label htmlFor="state">State</label>
                  <input
                    type="text"
                    id="state"
                    placeholder="State"
                    {...register("state", { required: "State is required" })}
                  />
                  {errors.state ? (
                    <p className="ua-field__error">{errors.state.message}</p>
                  ) : null}
                </div>

                <div className="ua-field">
                  <label htmlFor="zipcode">Zip code</label>
                  <input
                    type="text"
                    id="zipcode"
                    placeholder="Zip code"
                    {...register("zipcode", {
                      required: "Zipcode is required",
                    })}
                  />
                  {errors.zipcode ? (
                    <p className="ua-field__error">{errors.zipcode.message}</p>
                  ) : null}
                </div>

                <div className="ua-field">
                  <label htmlFor="country">Country</label>
                  <Controller
                    name="country"
                    control={control}
                    rules={{ required: "Country is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        inputId="country"
                        options={countryOptions}
                        placeholder="Select country"
                        classNamePrefix="react-select"
                      />
                    )}
                  />
                  {errors.country ? (
                    <p className="ua-field__error">{errors.country.message}</p>
                  ) : null}
                </div>

                <div className="ua-form-actions">
                  <button
                    type="button"
                    className="ua-btn ua-btn--ghost"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    className="ua-btn ua-btn--primary"
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                  <button
                    type="button"
                    className="ua-btn ua-btn--danger"
                    onClick={handleDeleteAccount}
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </form>
          )}
        </section>
      </UserAccountLayout>
    </>
  );
};

export default Userprofile;
